import React from 'react';

/** One label/value pair on the detail line; `danger` reddens the value. */
export interface LookupDetailItem {
  label: string;
  value?: string;
  danger?: boolean;
}

export interface LookupResultCardProps {
  name: string;
  /** Record id, shown top-right (e.g. #5946). */
  code: number | string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  /** Detail line — blank-valued items are skipped. */
  details?: LookupDetailItem[];
}

/**
 * Rich Bill To / Customer search-result row shown inline in the TypeAhead
 * dropdown: name + #id, the full address, and a detail line (Terms / Credit
 * Hold / AR Bal for bill-tos, etc.). Lets the user read the key details without
 * having to pick the record first. Transparent background — the dropdown option
 * supplies the row hover/selection styling.
 */
const LookupResultCard: React.FC<LookupResultCardProps> = ({
  name, code, address1, address2, city, state, zip, details = [],
}) => {
  const cityStateZip = [
    [city, state].filter(Boolean).join(', '),
    (zip ?? '').trim(),
  ].filter(Boolean).join(' ');
  const shown = details.filter((d) => d.value);
  return (
    <div className="relative w-full">
      <span className="absolute right-0 top-0 text-[0.65625rem] font-bold text-slate-400 dark:text-slate-500">#{code}</span>
      <div className="pr-10 text-[0.8125rem] font-bold text-slate-800 dark:text-slate-100">{name}</div>
      {address1 && <div className="text-[0.75rem] text-slate-600 dark:text-slate-300">{address1}</div>}
      {address2 && <div className="text-[0.75rem] text-slate-600 dark:text-slate-300">{address2}</div>}
      {cityStateZip && <div className="text-[0.75rem] text-slate-600 dark:text-slate-300">{cityStateZip}</div>}
      {shown.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[0.6875rem] text-slate-500 dark:text-slate-400">
          {shown.map((d) => (
            <span key={d.label}>
              {d.label}: <b className={d.danger ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-200'}>{d.value}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default LookupResultCard;
